import React from "react";
import { Link } from "react-router-dom";
import { ROUTES } from "@/config/constants";
import { FaFacebook, FaInstagram, FaTwitter, FaLinkedin } from "react-icons/fa";
import footerLogo from "@/assets/icons/Icon_1.png";

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const handleCookieSettings = () => {
    // Trigger cookie consent banner to reopen
    localStorage.removeItem("wih_media_cookie_consent");
    window.location.reload();
  };

  return (
    <footer className="bg-black text-white" role="contentinfo">
      <div className="section-container py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <img
                src={footerLogo}
                alt="WIH Media logo"
                className="w-32 h-32 rounded-lg object-contain"
                loading="lazy"
              />
            </div>
            <p className="text-gray-300 text-sm">
              Professional real estate photography and media services to showcase your properties in
              the best light.
            </p>
          </div>

          {/* Quick Links */}
          <nav aria-label="Quick links">
            <h3 className="font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2 text-sm" role="list">
              <li>
                <Link
                  to={ROUTES.HOME}
                  className="text-gray-300 hover:text-primary-500 transition-colors focus:outline-none focus:text-primary-500 focus:underline"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to={ROUTES.SERVICES}
                  className="text-gray-300 hover:text-primary-500 transition-colors focus:outline-none focus:text-primary-500 focus:underline"
                >
                  Services
                </Link>
              </li>
              <li>
                <Link
                  to={ROUTES.PORTFOLIO}
                  className="text-gray-300 hover:text-primary-500 transition-colors focus:outline-none focus:text-primary-500 focus:underline"
                >
                  Portfolio
                </Link>
              </li>
              <li>
                <Link
                  to={ROUTES.CONTACT}
                  className="text-gray-300 hover:text-primary-500 transition-colors focus:outline-none focus:text-primary-500 focus:underline"
                >
                  Contact
                </Link>
              </li>
            </ul>
          </nav>

          {/* Services */}
          <div>
            <h3 className="font-semibold mb-4">Services</h3>
            <ul className="space-y-2 text-sm" role="list">
              <li className="text-gray-300">Real Estate Photography</li>
              <li className="text-gray-300">Aerial/Drone Photography</li>
              <li className="text-gray-300">Website Creation</li>
              <li className="text-gray-300">Software Engineering</li>
              <li className="text-gray-300">Promotional Videos</li>
            </ul>
          </div>

          {/* Contact & Social */}
          <div>
            <h3 className="font-semibold mb-4">Connect With Us</h3>
            <address className="text-gray-300 text-sm mb-4 not-italic">
              <p>
                Email:{" "}
                <a
                  href="mailto:support@wihmedia.com"
                  className="hover:text-primary-500 transition-colors focus:outline-none focus:text-primary-500 focus:underline"
                >
                  support@wihmedia.com
                </a>
              </p>
              <p>
                Phone:{" "}
                <a
                  href="tel:+17729259109"
                  className="hover:text-primary-500 transition-colors focus:outline-none focus:text-primary-500 focus:underline"
                >
                  (772) 925-9109
                </a>
              </p>
            </address>
            <nav aria-label="Social media links">
              <ul className="flex space-x-4" role="list">
                <li>
                  <a
                    href="#"
                    className="text-gray-300 hover:text-primary-500 transition-colors p-2 -m-2 inline-flex items-center justify-center min-w-[44px] min-h-[44px] rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    aria-label="Follow us on Facebook"
                  >
                    <FaFacebook size={20} aria-hidden="true" />
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="text-gray-300 hover:text-primary-500 transition-colors p-2 -m-2 inline-flex items-center justify-center min-w-[44px] min-h-[44px] rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    aria-label="Follow us on Instagram"
                  >
                    <FaInstagram size={20} aria-hidden="true" />
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="text-gray-300 hover:text-primary-500 transition-colors p-2 -m-2 inline-flex items-center justify-center min-w-[44px] min-h-[44px] rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    aria-label="Follow us on Twitter"
                  >
                    <FaTwitter size={20} aria-hidden="true" />
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="text-gray-300 hover:text-primary-500 transition-colors p-2 -m-2 inline-flex items-center justify-center min-w-[44px] min-h-[44px] rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    aria-label="Follow us on LinkedIn"
                  >
                    <FaLinkedin size={20} aria-hidden="true" />
                  </a>
                </li>
              </ul>
            </nav>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-300">
          <div className="flex flex-wrap items-center justify-center gap-4 mb-4">
            <Link
              to={ROUTES.PRIVACY_POLICY}
              className="hover:text-primary-500 transition-colors focus:outline-none focus:text-primary-500 focus:underline"
            >
              Privacy
            </Link>
            <span className="hidden sm:inline text-gray-600">•</span>
            <Link
              to={ROUTES.TERMS_OF_SERVICE}
              className="hover:text-primary-500 transition-colors focus:outline-none focus:text-primary-500 focus:underline"
            >
              Terms
            </Link>
            <span className="hidden sm:inline text-gray-600">•</span>
            <button
              onClick={handleCookieSettings}
              className="hover:text-primary-500 transition-colors focus:outline-none focus:text-primary-500 focus:underline"
            >
              Cookie Preferences
            </button>
            <span className="hidden sm:inline text-gray-600">•</span>
            <Link
              to={ROUTES.EMAIL_PREFERENCES}
              className="hover:text-primary-500 transition-colors focus:outline-none focus:text-primary-500 focus:underline"
            >
              Email Preferences
            </Link>
          </div>
          <p>&copy; {currentYear} WhatisHyped Media LLC. All rights reserved.</p>
          <p className="text-xs mt-2">WhatisHyped Media LLC doing business as WIH Media</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
